import { nanoid } from 'nanoid';

import { database } from './environment';
import type { DPoPKey } from './types';
import { encodeUtf8, extractContentType, fromBase64Url, stringToSha256, toBase64Url } from './utils';

const ES256_ALG = { name: 'ECDSA', namedCurve: 'P-256' } as const;

/** Generates a fresh, non-extractable-by-default ES256 DPoP key. */
export const createES256Key = async (): Promise<DPoPKey> => {
	const pair = await crypto.subtle.generateKey(ES256_ALG, true, ['sign', 'verify']);

	const key = await crypto.subtle.exportKey('pkcs8', pair.privateKey);
	const { ext: _ext, key_ops: _keyOps, ...jwk } = await crypto.subtle.exportKey('jwk', pair.publicKey);

	return {
		typ: 'ES256',
		key: toBase64Url(new Uint8Array(key)),
		jwt: toBase64Url(encodeUtf8(JSON.stringify({ typ: 'dpop+jwt', alg: 'ES256', jwk: jwk }))),
	};
};

export const createDPoPSignage = (dpopKey: DPoPKey) => {
	const headerString = dpopKey.jwt;
	const keyPromise = crypto.subtle.importKey(
		'pkcs8',
		fromBase64Url(dpopKey.key) as Uint8Array<ArrayBuffer>,
		ES256_ALG,
		true,
		['sign'],
	);

	const constructPayload = (htm: string, htu: string, nonce: string | undefined, ath: string | undefined) => {
		const payload = {
			ath: ath,
			htm: htm,
			htu: htu,
			iat: Math.floor(Date.now() / 1_000),
			jti: nanoid(24),
			nonce: nonce,
		};

		return toBase64Url(encodeUtf8(JSON.stringify(payload)));
	};

	return async (method: string, htu: string, nonce: string | undefined, ath: string | undefined) => {
		const payloadString = constructPayload(method, htu, nonce, ath);

		const signed = await crypto.subtle.sign(
			{ name: 'ECDSA', hash: { name: 'SHA-256' } },
			await keyPromise,
			encodeUtf8(headerString + '.' + payloadString) as Uint8Array<ArrayBuffer>,
		);

		const signatureString = toBase64Url(new Uint8Array(signed));

		return headerString + '.' + payloadString + '.' + signatureString;
	};
};

/**
 * Wraps `fetch` with DPoP proof generation and nonce handling. Mirrors the
 * upstream `@atcute/oauth-browser-client` behaviour.
 */
export const createDPoPFetch = (dpopKey: DPoPKey, isAuthServer?: boolean): typeof fetch => {
	const nonces = database.dpopNonces;
	const pending = database.inflightDpop;

	const sign = createDPoPSignage(dpopKey);

	return async (input, init) => {
		const request = new Request(input as any, init);

		const authorizationHeader = request.headers.get('authorization');
		const ath = authorizationHeader?.startsWith('DPoP ')
			? await stringToSha256(authorizationHeader.slice(5))
			: undefined;

		const { method, url } = request;
		const { origin, pathname } = new URL(url);

		const htu = origin + pathname;

		let deferred = pending.get(origin);
		if (deferred) {
			await deferred.promise;
			deferred = undefined;
		}

		let initNonce: string | undefined;
		let expiredOrMissing = false;
		try {
			const [nonce, lapsed] = nonces.getWithLapsed(origin);

			initNonce = nonce;
			// The reference PDS rotates DPoP nonces every ~3 minutes; if it's been
			// longer than that (or we never had one), let this request through and
			// defer concurrent ones until we have a fresh nonce.
			expiredOrMissing = lapsed > 3 * 60 * 1_000;
		} catch {
			// Ignore read errors, act as if we're missing a nonce.
		}

		if (expiredOrMissing) {
			pending.set(origin, (deferred = Promise.withResolvers()));
		}

		let nextNonce: string | null;
		try {
			const initProof = await sign(method, htu, initNonce, ath);
			request.headers.set('dpop', initProof);

			const initResponse = await fetch(request);

			nextNonce = initResponse.headers.get('dpop-nonce');
			if (nextNonce === null || nextNonce === initNonce) {
				return initResponse;
			}

			try {
				nonces.set(origin, nextNonce);
			} catch {
				// Ignore write errors
			}

			const shouldRetry = await isUseDpopNonceError(initResponse, isAuthServer);
			if (!shouldRetry) {
				return initResponse;
			}

			if (input === request || init?.body instanceof ReadableStream) {
				// Stream already consumed; can't safely retry.
				return initResponse;
			}
		} finally {
			if (deferred) {
				pending.delete(origin);
				deferred.resolve();
			}
		}

		{
			const nextProof = await sign(method, htu, nextNonce, ath);
			const nextRequest = new Request(input as any, init);
			nextRequest.headers.set('dpop', nextProof);

			const retryResponse = await fetch(nextRequest);

			const retryNonce = retryResponse.headers.get('dpop-nonce');
			if (retryNonce !== null && retryNonce !== nextNonce) {
				try {
					nonces.set(origin, retryNonce);
				} catch {
					// Ignore write errors
				}
			}

			return retryResponse;
		}
	};
};

const isUseDpopNonceError = async (response: Response, isAuthServer?: boolean): Promise<boolean> => {
	if (isAuthServer === undefined || isAuthServer === false) {
		if (response.status === 401) {
			const wwwAuth = response.headers.get('www-authenticate');
			if (wwwAuth?.startsWith('DPoP')) {
				return wwwAuth.includes('error="use_dpop_nonce"');
			}
		}
	}

	if (isAuthServer === undefined || isAuthServer === true) {
		if (response.status === 400 && extractContentType(response.headers) === 'application/json') {
			try {
				const json = await response.clone().json();
				return typeof json === 'object' && json?.['error'] === 'use_dpop_nonce';
			} catch {
				return false;
			}
		}
	}

	return false;
};
